/* eslint-disable no-underscore-dangle */
/* eslint-disable max-lines */
/* eslint-disable import/no-duplicates */
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Buffer } from 'buffer';
import cloudUpload from '../../images/cloud-stmt-upload.svg';
import checkMark from '../../images/checkmark-circle.svg';
import gallery from '../../images/gallery.svg';
import deleteIcon from '../../images/delete-icon.svg';
import repeat from '../../images/repeat.png';
import UploadImageEN from '../../images/Upload_GIF_2.gif';
import UploadImageES from '../../images/Upload_GIF_2ES.gif';
import { SidebarStyles as classes } from './SidebarStyles';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import Loader from '../Loader/Loader';
import CustomTooltip from '../Tooltip/CustomTooltip';
import {
  ELITE_ACCOUNT_EN,
  FUND_UPLOAD_EXPIRED_CHECK,
  FUND_UPLOAD_VOID_CHECK,
} from '../../constants';
import { dgiMapping } from '../../services/dgiMapping';

import {
  generateFileMD5,
  generateFileMD5Cordova,
  uploadDocument,
  uploadDocumentByPreSignedURL,
  uploadDocumentXL,
} from '../../services/uploadService';
import { productNameURLMapping } from '../../services/productNameURLMapping';
import { businessMapping } from '../../services/businessMapping';
import { StatementUploadStyles as statementClasses } from './StatementUploadStyles';
import { productMapping } from '../../services/productMapping';
import {
  getCordovaPlatformId,
  isCDIraproduct,
  isCordovaFn,
  isIRAEducativaProduct,
  isIRAProduct,
  isPlaidProduct,
  isWebComponentFlow,
  resolveFileToBlob,
} from '../commonCode';
import { CloudUpload, CloudUploadBtn } from '../../images/svg/CustomizedSVG';
import { handleApiEnviroment } from '../../WebComponents/commonComponents/customUtils';

const preSignedUrlResponse = (resp, documentId, baseContext) => {
  const {
    setStatementDocumentId,
    setStatementUploadLoading,
    setStatementUploadStatus,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setStatementError,
    setPlaidStatementMandatoryError,
  } = baseContext;
  if (resp && resp.status === 200) {
    setStatementDocumentId(documentId);
    setStatementUploadLoading(false);
    setStatementUploadStatus('DONE');
    setStatementError('');
    setPlaidStatementMandatoryError(false);
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    baseContext.setFundDataErrors((prevState) => ({
      ...prevState,
      statementDocumentId: '',
    }));
  } else {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
  }
};
const documentResponse = async (
  fileMD5,
  fileExtension,
  fileName,
  fileType,
  fileSize,
  file,
  baseContext,
  isIOSCordova = false // ← NEW: threaded from uploadFileStatement
) => {
  const { setStatementUploadLoading, setStatementUploadStatus } = baseContext;

  const fileMd5Base64 = Buffer.from(fileMD5, 'hex').toString('base64');

  const response = await uploadDocumentXL(
    fileExtension,
    fileName,
    fileType,
    fileSize,
    fileMd5Base64,
    baseContext.accessToken,
    baseContext.setStatementUploadProgress,
    baseContext.setStatementLinearProgress,
    baseContext.initiateUiResponse,
    { isWCFlow: baseContext?.wcFlow, env: handleApiEnviroment(baseContext?.environment) }
  );

  if (response?.data) {
    const documentId = response.data.documentId ? response.data.documentId : null;
    const preSignedUrl = response.data.preSignedUrl ? response.data.preSignedUrl : null;
    if (preSignedUrl && documentId) {
      let resp;
      try {
        // ── Android / Web: unchanged ─────────────────────────────────────
        resp = await uploadDocumentByPreSignedURL(
          preSignedUrl,
          file,
          fileMd5Base64,
          baseContext.setStatementUploadProgress,
          baseContext.setStatementLinearProgress
        );
      } catch (err) {
        console.error('[StatementUpload] Pre-signed URL upload threw', err);
        console.log('[StatementUpload] Pre-signed URL upload threw', err);

        setStatementUploadLoading(false);
        setStatementUploadStatus('FAILED');
        return;
      }

      preSignedUrlResponse(resp, documentId, baseContext);
    } else {
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
    }
  } else {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
  }
};

/**
 * FIX: ERR_UPLOAD_FILE_CHANGED in StatementUpload.jsx (uploadFileStatement)
 *
 * SAME ROOT CAUSE as CommonDocumentUpload:
 * Google Drive / cloud files on Android Cordova are backed by a content:// URI
 * that expires after the first read. Both upload paths (POST <3MB and PUT >3MB)
 * re-read the original file reference during the actual HTTP transfer — by that
 * point the URI has been consumed → ERR_UPLOAD_FILE_CHANGED.
 *
 * THE FIX — identical pattern to CommonDocumentUpload:
 * Add materializeFileToStableBlob() and call it ONCE, right after validation,
 * before either upload path runs. Both paths then use the in-memory stableFile.
 *
 * CHANGES FROM ORIGINAL (minimal, surgical):
 *  1. Add materializeFileToStableBlob helper (copy from CommonDocumentUpload — same function)
 *  2. After extension/size validation, await materializeFileToStableBlob(file)
 *  3. <3MB path: formData.append('file', stableFile, fileName)  ← was raw file
 *  4. >3MB path: remove resolveFileToBlob call (already done by materialize step)
 *               pass stableFile directly to generateFileMD5Cordova1 + documentResponse
 *
 * NO other changes — all state setters, error messages, service calls unchanged.
 */

/**
 * Reads any File/Blob into a stable in-memory Blob by eagerly copying bytes
 * through FileReader. After this resolves, the result is 100% RAM-backed and
 * cannot go stale regardless of what happens to the original content:// URI.
 *
 * - If input is already a real Blob: copies bytes via FileReader (detaches URI)
 * - If input is a Cordova string URI: delegates to resolveFileToBlob first,
 *   then copies through FileReader
 *
 * @param {File|Blob|string} file
 * @param {string} mimeType
 * @returns {Promise<Blob>}
 */
const materializeFileToStableBlob = (file, mimeType) =>
  new Promise((resolve, reject) => {
    // iOS fast path: bytes already read synchronously in fileInputHandler
    // before any await — security-scoped URL was still valid at that point.
    console.log({ filepath_iosBuffer: file.__iosArrayBuffer });
    console.log('mime', mimeType);
    console.log('file', file);
    if (file.__iosArrayBuffer) {
      try {
        resolve(new Blob([file.__iosArrayBuffer], { type: mimeType || file.type }));
      } catch (e) {
        reject(e);
      }
      return;
    }

    // Android / Web: standard FileReader copy.
    // For Android this detaches bytes from the content:// URI.
    // For Web the File is already memory-backed — this is a safe copy.
    if (file instanceof Blob && file.size > 0) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(new Blob([reader.result], { type: mimeType || file.type }));
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
      return;
    }

    // Cordova file:// string URI fallback (camera plugin etc.)
    resolveFileToBlob(file)
      .then((blob) => materializeFileToStableBlob(blob, mimeType))
      .catch(reject);
  });

/**
 * DROP-IN REPLACEMENT for uploadFileStatement in StatementUpload.jsx
 *
 * Diff summary vs original:
 *  + materializeFileToStableBlob helper added above this function
 *  + stableFile materialization block added after validation (lines marked NEW)
 *  ~ <3MB: formData.append uses stableFile + explicit fileName  (was raw file)
 *  ~ >3MB: resolveFileToBlob call removed; stableFile used directly
 *  = everything else byte-for-byte identical
 */
export const uploadFileStatement = async (file, baseContext, props) => {
  const {
    setStatementUploadStatus,
    setStatementDocumentId,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setStatementUploadLoading,
    setModalMessageGlobal,
    modalMessageGlobal,
    setPlaidStatementMandatoryError,
    setIsErrorGlobal,
    setStatementFileName,
    setStatementError,
    setStatementLinearProgress,
    setStatementFileSize,
  } = baseContext;

  const platformId = getCordovaPlatformId();
  const isIOSCordova = platformId === 'ios'; // ← single source of truth

  setStatementUploadLoading(true);
  const fileName = file.name;
  const { fileType } = props;
  setStatementFileName(fileName);
  const fileParts = fileName.split('.');
  const fileExtension = fileParts[fileParts.length - 1].toLowerCase();
  const fileSize = file.size;
  setStatementFileSize(fileSize);

  // ── Size guard (unchanged) ────────────────────────────────────────────────
  if (fileSize > 10485760) {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
    setModalMessageGlobal({
      ...modalMessageGlobal,
      messageTitle: 'Document uploaded is not supported.',
      messageSubTitle: 'The file exceeds the maximum upload size (10 Mb).',
      typeName: 'statementUpload',
    });
    setStatementError('exceeded');
    setIsErrorGlobal(true);
    return;
  }

  // ── Extension guard (unchanged) ───────────────────────────────────────────
  const extensions = ['pdf', 'jpg', 'jpeg', 'png'];
  const isValidExtension = extensions.includes(fileExtension.toLowerCase());

  if (!isValidExtension) {
    setStatementUploadLoading(false);
    setStatementUploadStatus('FAILED');
    setStatementError('invalid format');
    setModalMessageGlobal({
      ...modalMessageGlobal,
      messageSubTitle: 'Only files with the following formats are allowed: PNG, JPG, PDF.',
      typeName: 'statementUpload',
    });
    setIsErrorGlobal(true);
    return;
  }

  if (fileSize < 10485760 && isValidExtension) {
    setStatementLinearProgress(true);
  }

  // ── Materialize (unchanged) ───────────────────────────────────────────────
  let stableFile;
  const isCordovaFunc = isCordovaFn();
  if (isCordovaFunc) {
    try {
      stableFile = await materializeFileToStableBlob(file, file.type);
      console.info(
        `[StatementUpload] File materialized: ${fileName} (${stableFile.size} bytes) on ${
          platformId || 'web'
        }`
      );
    } catch (err) {
      console.error(`[StatementUpload] Failed to materialize file on ${platformId || 'web'}`, err);
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
      setIsErrorGlobal(true);
      return;
    }
  }

  const selectedFile = isCordovaFunc ? stableFile : file;
  // ── Path 1: File > 3MB → MD5 + PreSigned URL ─────────────────────────────
  if (fileSize > 3145728) {
    try {
      console.info(`[StatementUpload] >3MB on ${platformId || 'web'}`);

      const fileMD5 = isCordovaFunc
        ? await generateFileMD5Cordova(stableFile)
        : await generateFileMD5(file);
      console.log({ fileMD5 });
      await documentResponse(
        fileMD5,
        fileExtension,
        fileName,
        fileType,
        fileSize,
        selectedFile,
        baseContext,
        isIOSCordova // ← NEW: lets documentResponse choose XHR vs Axios
      );
    } catch (err) {
      console.error(`[StatementUpload] >3MB upload failed on ${platformId || 'web'}`, err);
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
      setIsErrorGlobal(true);
    }
  } else {
    // ── Path 2: File ≤ 3MB → direct multipart POST (unchanged) ──────────────
    const formData = new FormData();
    formData.append('file', selectedFile, fileName);
    console.log('formData', formData);

    const response = await uploadDocument(
      fileExtension,
      fileName,
      fileType,
      formData,
      baseContext.accessToken,
      baseContext.setStatementUploadProgress,
      baseContext.setStatementLinearProgress,
      baseContext.initiateUiResponse,
      {
        isWCFlow: baseContext?.wcFlow,
        env: handleApiEnviroment(baseContext?.environment),
      }
    );

    if (response?.data) {
      setStatementDocumentId(response.data.documentId);
      setStatementUploadLoading(false);
      setStatementUploadStatus('DONE');
      setStatementError('');
      setPlaidStatementMandatoryError(false);
      setIsFundingScreenDirty(true);
      setIsEdeliveryScreenDirty(true);
      baseContext.setFundDataErrors((prevState) => ({
        ...prevState,
        statementDocumentId: '',
      }));
    } else {
      setStatementUploadLoading(false);
      setStatementUploadStatus('FAILED');
    }
  }
};
function StatementUpload(props) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const baseContext = isWebComponentFlow();
  const { pagePath, history, isBundle } = props;
  const {
    statementUploadStatus,
    productName,
    fundData,
    statementUploadLoading,
    setStatementUploadStatus,
    setStatementFileName,
    statementFileName,
    statementError,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setFundData,
    setStatementDocumentId,
    setFundDataErrors,
    setStatementFileSize,
    setStatementError,
    statementUploadProgress,
    setStatementLinearProgress,
    setBankUploadPreviewUrl,
    theme,
    plaidDetails,
    language,
  } = baseContext;
  const isVerificationPending =
    plaidDetails?.remarks === 'pending_manual_verification' && plaidDetails?.status === 'Success';
  const isPlaidSuccess = plaidDetails?.status === 'Success' && !plaidDetails?.remarks;

  const fileInput = useRef(null);

  const uploadStatementImage = baseContext.language === 'EN' ? UploadImageEN : UploadImageES;

  const [flowCheck, setFlowCheck] = useState('');
  useEffect(() => {
    if (businessMapping(productName)) {
      setFlowCheck('commercial-form');
    } else {
      setFlowCheck('retail-form');
    }
  }, []);
  const checkFileInput = () => {
    try {
      if (fileInput.current && fileInput?.current?.value) {
        fileInput.current.value = '';
      }
    } catch (err) {
      console.warn('File input reset failed:', err);
    }
  };

  const buttonClickHandler = (e) => {
    setTimeout(() => {
      checkFileInput();
      fileInput.current?.click();
    }, 0);
  };
  const removeUploadedFile = () => {
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    setStatementUploadStatus(null);
    setStatementFileName('');
    setStatementFileSize('');
    setStatementError('');
    setStatementDocumentId(null);
    setBankUploadPreviewUrl('');

    if (
      productMapping(baseContext.productName) === ELITE_ACCOUNT_EN ||
      productMapping(baseContext.productName) === 'Ahorra' ||
      isIRAProduct(baseContext.productName)
    ) {
      setFundData((prevState) => ({
        ...prevState,
        accountNumber: '',
        typeOfAccount: '',
        bank: '',
        otherBank: '',
        transitNumber: '',
      }));
      setFundDataErrors((prevState) => ({
        ...prevState,
        accountNumber: '',
        typeOfAccount: '',
        bank: '',
        otherBank: '',
        transitNumber: '',
      }));
    }
  };

  useEffect(() => {
    if (statementUploadProgress === 100) {
      const timeout = setTimeout(() => {
        setStatementLinearProgress(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [statementUploadProgress]);

  let status;
  const uploadStatusNotDone = () => {
    if (statementUploadStatus !== 'DONE') {
      status = '0';
    } else {
      status = '-1';
    }
    return status;
  };

  let uploadStatement;
  const uploadStatementGif = () => {
    if (statementUploadStatus !== 'DONE' && !statementUploadLoading) {
      uploadStatement = (
        <Box component="div" sx={classes.new_addr_gif}>
          <img src={uploadStatementImage} alt={t('Upload statement funding screen')} />
        </Box>
      );
      // } else {
      //   uploadStatement = (
      //     <div className={classes.new_addr_gif}>
      //       <img
      //         tabIndex="0"
      //         className={classes.new_addr_gif_width_adjust}
      //         src={uploadStatementImage}
      //         alt={t('Upload document funding screen')}
      //       />
      //     </div>
      //   );
      // }
    }
    return uploadStatement;
  };

  const fileInputHandler = async (e) => {
    e.preventDefault();
    const file = e?.target?.files?.[0];
    if (file === undefined || file === null) return;

    // ── iOS CORDOVA ONLY: Synchronous eager-read ──────────────────────────────
    //
    // iOS revokes the security-scoped URL backing the File object within
    // milliseconds of the picker closing (DocumentManager thumbnail crash:
    // QLThumbnailErrorDomain Code=102 → UIViewServiceErrorDomain Code=1).
    //
    // FileReader.readAsArrayBuffer(file) MUST be called here, synchronously,
    // before any await — including the implicit yield inside the createObjectURL
    // try/catch block below. iOS keeps the scope alive for any read already
    // in-progress at revocation time.
    //
    // Android and Web are unaffected — their file URLs do not get revoked.
    // ─────────────────────────────────────────────────────────────────────────
    if (getCordovaPlatformId() === 'ios') {
      const iosEagerRead = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file); // synchronous — no await before this line
      });

      try {
        const arrayBuffer = await iosEagerRead;
        Object.defineProperty(file, '__iosArrayBuffer', {
          value: arrayBuffer,
          writable: false,
          enumerable: false,
          configurable: false,
        });
        console.info(
          `[StatementUpload] [iOS] eager-read OK: ${file.name} (${arrayBuffer.byteLength} bytes)`
        );
      } catch (readErr) {
        console.error('[StatementUpload] [iOS] eager-read failed', readErr);
        // Upload cannot proceed — file bytes are unreadable.
        // Let uploadFileStatement handle the FAILED state via its own error path.
        return;
      }
    }
    // ── END iOS EAGER-READ ────────────────────────────────────────────────────

    // Preview URL — non-critical, failure must never block upload.
    // On iOS the objectURL may still fail (WKWebView sometimes rejects it for
    // large files) but bytes are already captured in __iosArrayBuffer above.
    try {
      setBankUploadPreviewUrl(URL.createObjectURL(file));
    } catch (urlErr) {
      console.warn(
        `[StatementUpload] createObjectURL failed on platform: ${getCordovaPlatformId() || 'web'}`,
        urlErr
      );
      setBankUploadPreviewUrl('');
    }

    // uploadFileStatement reads the file via materializeFileToStableBlob which
    // will use __iosArrayBuffer on iOS — no further security-scope dependency.
    uploadFileStatement(file, baseContext, props);
  };

  const routeToCameraUpload = () => {
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    if (baseContext?.wcFlow) {
      history.push({
        pathname: `/camera-upload`,
        redirectingPath: pagePath,
        pageProps: props,
        flow: flowCheck,
      });
    } else {
      history.push({
        pathname: `/${baseContext.location?.toLowerCase()}/${baseContext.language?.toLowerCase()}/${flowCheck}/${productNameURLMapping(
          productName
        )}/camera-upload`,
        redirectingPath: pagePath,
        pageProps: props,
        flow: flowCheck,
      });
    }
  };
  const platformId = getCordovaPlatformId();

  const isCordova = platformId === 'ios' || platformId === 'android';
  return (
    <Grid container style={{ height: '100%', width: '100%' }}>
      <Grid
        sx={{
          ...(isBundle ? statementClasses.mainContainerBundle : statementClasses.mainContainer),
          backgroundColor: isBundle ? theme?.palette?.plaidCardBg?.main : '#F8F8F8',
          border: isBundle
            ? `1px solid ${theme?.palette?.plaidCardBorder?.main}`
            : '1px solid #D0D0D0',
          borderRadius: isBundle ? '24px' : '4px',
        }}
        aria-label={t('Upload Bank Statement', {
          defaultValue: 'Upload Bank Statement',
          nsSeparator: '|',
        })}
      >
        {/* <img
          src={cloudUpload}
          height="42px"
          width="42px"
          alt={uploadBankStatementAltVal(leadGenerationContext.language, statementUploadStatus)}
        /> */}
        <div style={{ width: '45px', height: '45px' }}>
          <CloudUploadBtn
            height="100%"
            width="100%"
            color={theme?.palette?.orientalPrimary?.main}
          />
        </div>

        <Typography
          // component="h5"
          data-testid="statement-proof"
          sx={
            isBundle
              ? {
                  ...statementClasses.Bundletext1,
                  fontSize: language === 'en' ? '20px !important' : '18px !important',
                  color: theme?.palette?.primary?.main,
                  alignItems: 'center',
                }
              : statementClasses.text1
          }
          aria-label={t('Upload Bank Statement', {
            defaultValue: 'Upload Bank Statement',
            nsSeparator: '|',
          })}
        >
          {t('Upload Bank Statement', {
            defaultValue: 'Upload Bank Statement',
            nsSeparator: '|',
          })}
          {/* <span
            style={{
              fontSize: '22px',
              color: ORIENTAL_ORANGE,
              position: 'absolute',
              right: '23px',
              top: '-8px',
            }}
          >
            *
          </span> */}
          <span style={{ marginLeft: isBundle ? '5px' : '15px', opacity: 1, cursor: 'pointer' }}>
            {dgiMapping(productName) ? (
              <CustomTooltip
                placement="top"
                text={t(`PreviewStatementDgi`, {
                  defaultValue: `Sensitive Information will be encrypted and safeguarded in our systems. A blank Void check or bank statement from the last 60 days is required to confirm your account. The numbering for the void check must be equal to or greater than 100. If the account is not currently under your name, additional information will be requested.`,
                })}
              />
            ) : (
              <CustomTooltip
                placement="top"
                text={t(`PreviewStatement`, {
                  defaultValue: `Sensitive Information will be encrypted and safeguard in our systems. A blank Void check or bank statement from the last 60 days is required to confirm your account.`,
                })}
              />
            )}
          </span>
        </Typography>
        {statementUploadStatus !== 'DONE' && (
          <input
            type="file"
            data-testid="inp_test"
            accept={
              isCordova
                ? '*'
                : 'application/pdf,.pdf,image/jpeg,.jpg,.jpeg,.jfif,image/png,.png,image/bmp,.bmp,.bpm,image/x-portable-graymap,.pgm,image/x-portable-pixmap,.ppm,image/gif,.gif,.gifv,image/tiff,.tiff,image/heic,.heic,image/heif,.heif,video/mpeg,.mpg,.mpeg,.hevc'
            }
            onChange={(e) => fileInputHandler(e, baseContext, props)}
            ref={fileInput}
            style={{ display: 'none' }}
          />
        )}
        {statementUploadLoading && (
          <Box component="div" sx={classes.errorImgPosition}>
            <Loader />
          </Box>
        )}
        {statementUploadStatus === null && !statementUploadLoading && (
          <>
            <Typography
              color="subtext"
              variant={isBundle ? 'body1' : 'subtitle1'}
              component="p"
              id="uploadVoidCheck"
              sx={{
                ...(isBundle ? statementClasses.Bundletext2 : statementClasses.text2),
                ...(isBundle
                  ? language === 'EN'
                    ? {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        margin: '0px !important',
                      }
                    : {
                        marginBottom: '20px',
                      }
                  : {
                      marginBottom: '25px',
                    }),
              }}
            >
              {isBundle
                ? t(FUND_UPLOAD_EXPIRED_CHECK, {
                    defaultValue: FUND_UPLOAD_EXPIRED_CHECK,
                    nsSeparator: '|',
                  })
                : t(FUND_UPLOAD_VOID_CHECK, {
                    defaultValue: FUND_UPLOAD_VOID_CHECK,
                    nsSeparator: '|',
                  })}
            </Typography>

            <Box component="div" sx={statementClasses.btnContainer}>
              <div>
                <Button
                  disableFocusRipple
                  id="uploadDocId"
                  variant="outlined"
                  color="primary"
                  data-testid="null_testid"
                  aria-describedby="uploadVoidCheck"
                  onClick={buttonClickHandler}
                  tabIndex={uploadStatusNotDone()}
                  disabled={isPlaidSuccess || isVerificationPending}
                  classes={{ disabled: statementClasses.disabledButton }}
                  sx={
                    isBundle
                      ? {
                          ...statementClasses.uploadBtnBundle,
                          ...(language === 'ES' ? { marginTop: '0px' } : { marginTop: '32px' }),
                          color: theme?.palette?.orientalPrimary?.main,
                          border: `1.6px solid ${theme?.palette?.orientalPrimary?.main}`,
                          ':focus': {
                            outline: `2px solid ${theme?.palette?.orientalPrimary?.main}`,
                            outlineOffset: '2px',
                          },
                          '&.MuiButton-root.Mui-disabled': {
                            background: theme?.palette?.disabledButtonBackground?.main,
                            borderRadius: '56px',
                            color: theme?.palette?.disabledButton?.main,
                            border: 'none',
                          },
                        }
                      : statementClasses.uploadBtn
                  }
                >
                  {t('Upload Now', {
                    defaultValue: 'Upload Now',
                  })}
                </Button>
              </div>
              {!isBundle && (
                <div>
                  <Button
                    disableFocusRipple
                    color="inherit"
                    variant="outlined"
                    onClick={routeToCameraUpload}
                    disabled={isPlaidSuccess || isVerificationPending}
                    data-testid="camera_testid"
                    sx={statementClasses.cameraBtn}
                  >
                    {t('Camera', {
                      defaultValue: 'Camera',
                    })}
                  </Button>
                </div>
              )}
            </Box>
            {!isBundle && (
              <Typography variant="body1" sx={statementClasses.text3}>
                {t('Supported file types: PNG, JPG, PDF. Maximum: 10MB', {
                  defaultValue: 'Supported file types: PNG, JPG, PDF. Maximum: 10MB',
                  nsSeparator: '|',
                })}
              </Typography>
            )}
          </>
        )}
        {statementUploadStatus === 'DONE' &&
          !statementUploadLoading &&
          (isBundle ? (
            <>
              <Typography sx={statementClasses.Bundletext2} id="uploadVoidCheck">
                {t(FUND_UPLOAD_EXPIRED_CHECK, {
                  defaultValue: FUND_UPLOAD_EXPIRED_CHECK,
                  nsSeparator: '|',
                })}
              </Typography>

              <Box component="div" sx={statementClasses.btnContainer}>
                <div>
                  <Button
                    disableFocusRipple
                    color="inherit"
                    id="uploadDocId"
                    variant="outlined"
                    aria-describedby="uploadVoidCheck"
                    onClick={buttonClickHandler}
                    tabIndex={uploadStatusNotDone()}
                    disabled={
                      isPlaidSuccess || isVerificationPending || statementUploadStatus === 'DONE'
                    }
                    sx={{
                      ...(isBundle ? statementClasses.uploadBtnBundle : statementClasses.uploadBtn),
                      ...(language === 'ES' ? { marginTop: '18px' } : { marginTop: '32px' }),
                      '&.Mui-disabled': {
                        backgroundColor: theme?.palette?.disabledButtonBackground?.main,
                        borderRadius: '56px',
                        color: theme?.palette?.disabledButton?.main,
                        border: 'none',
                        pointerEvents: 'none',
                        cursor: 'default',
                        ':hover': {
                          backgroundColor: theme?.palette?.disabledButtonBackground?.main,
                        },
                        ':focus': {
                          outline: 'none',
                        },
                      },
                    }}
                    data-testid="upload-now-id"
                  >
                    {t('Upload Now', {
                      defaultValue: 'Upload Now',
                    })}
                  </Button>
                </div>
              </Box>
            </>
          ) : (
            <Box component="div" sx={statementClasses.uploadDone}>
              <img src={gallery} height="24px" width="24px" alt="success icon" />
              <div>
                <Typography sx={statementClasses.fileName}>{statementFileName}</Typography>
                <Typography sx={statementClasses.uploadSuccessMssg}>
                  {t('Uploaded successfully', {
                    defaultValue: 'Uploaded successfully',
                    nsSeparator: '|',
                  })}
                </Typography>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <img
                  src={checkMark}
                  height="18px"
                  width="18px"
                  alt="success check mark"
                  style={{ cursor: 'pointer' }}
                />
                <img
                  src={deleteIcon}
                  height="18px"
                  width="18px"
                  alt="delete icon"
                  style={{ cursor: 'pointer' }}
                  onClick={removeUploadedFile}
                  onKeyDown={removeUploadedFile}
                />
              </div>
            </Box>
          ))}
        {statementUploadStatus === 'FAILED' && !statementUploadLoading && !isBundle && (
          <Box component="div" sx={statementClasses.uploadFail}>
            <img src={gallery} height="24px" width="24px" alt="success icon" />
            <div>
              <Typography sx={statementClasses.fileName}>{statementFileName}</Typography>

              <Typography sx={statementClasses.uploadFailMssg}>
                {statementError === 'exceeded'
                  ? t('Upload failed.File size exceeded', {
                      defaultValue: 'Upload failed.File size exceeded',
                      nsSeparator: '|',
                    })
                  : t('Upload failed. File format not supported', {
                      defaultValue: 'Upload failed. File format not supported',
                      nsSeparator: '|',
                    })}
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img
                src={repeat}
                height="20px"
                width="20px"
                style={{ cursor: 'pointer' }}
                id="uploadAddressProofFail"
                data-testid="failed_id"
                onClick={buttonClickHandler}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    buttonClickHandler(e);
                  }
                }}
                alt="Upload Failed"
              />
              <img
                src={deleteIcon}
                height="18px"
                width="18px"
                alt="delete icon"
                style={{ cursor: 'pointer' }}
                onClick={removeUploadedFile}
                onKeyDown={removeUploadedFile}
              />
            </div>
          </Box>
        )}
      </Grid>

      {!isPlaidProduct(productName) && (
        <Grid size={{ xs: 12 }} sx={classes.id_upload_grid}>
          {fundData?.statementUpload ? uploadStatementGif() : null}
        </Grid>
      )}
    </Grid>
  );
}
export default withRouter(StatementUpload);
StatementUpload.propTypes = {
  // fileType: PropTypes.string,
  pagePath: PropTypes.string,
  isBundle: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};
